import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, CheckCircle2, MessageCircle, CalendarDays, List, CalendarRange, RefreshCw } from "lucide-react";
import { Chip, Spinner } from "@/components/peeps/ui";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { todayISO, longDate, rangeBounds, inRange } from "@/lib/date";
import { AgendaItem } from "@/components/peeps/AgendaItem";
import { MonthCalendar } from "@/components/peeps/MonthCalendar";
import { TabLayout } from "@/components/peeps/TabLayout";
import { HomeButton } from "@/components/peeps/HomeButton";

const pad = (n) => String(n).padStart(2, "0");
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const localDay = (s) => {
  if (!s) return "";
  const str = String(s);
  if (str.length <= 10) return str.slice(0, 10);
  const d = new Date(str);
  return isNaN(d.getTime()) ? str.slice(0, 10) : isoOf(d);
};
const clockOf = (ev) => {
  if (ev.all_day) return "All day";
  try { return new Date(ev.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
};

export default function Agenda() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list");
  const [range, setRange] = useState("all");     // today | week | month | all
  const [showPassed, setShowPassed] = useState(false);
  const navigate = useNavigate();

  // calendar-view state
  const [cursor, setCursor] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [gcal, setGcal] = useState({ connected: true, events: [] });
  const [gLoading, setGLoading] = useState(false);
  const [gError, setGError] = useState(null);

  const load = useCallback(async () => {
    try { setItems(await api.myAgenda()); } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadGoogle = useCallback(async () => {
    setGLoading(true);
    setGError(null);
    const start = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
    const end = new Date(cursor.getFullYear(), cursor.getMonth() + 2, 1);
    try {
      const res = await api.myCalendarEvents(start.toISOString(), end.toISOString());
      setGcal(res);
      if (res.error) setGError(res.error);
    } catch (e) {
      setGError("Couldn't load your Google Calendar.");
    } finally {
      setGLoading(false);
    }
  }, [cursor]);

  useEffect(() => { if (view === "calendar") loadGoogle(); }, [view, loadGoogle]);

  // Reflect Google-side changes as soon as the user returns: refetch plans +
  // Google events on focus/visibility (server reads Google live per request).
  const refresh = useCallback(() => { load(); if (view === "calendar") loadGoogle(); }, [load, loadGoogle, view]);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", onVisible); };
  }, [refresh]);

  const [showImports, setShowImports] = useState(true);
  const visibleItems = useMemo(() => (showImports ? items : items.filter((e) => e.source !== "chat_import")), [items, showImports]);

  const split = useMemo(() => {
    const today = todayISO();
    const b = rangeBounds(range);
    let list = visibleItems.filter((e) => inRange(e.date, b));
    if (filter === "going") list = list.filter((e) => e.im_going);
    if (filter === "commented") list = list.filter((e) => e.commented);
    const upcoming = list.filter((e) => e.date >= today);
    const passed = list.filter((e) => e.date < today).reverse();
    return { upcoming, passed };
  }, [visibleItems, filter, range]);

  // "Added from chat" import layer — toggleable like Google events.
  const hasImports = useMemo(() => items.some((e) => e.source === "chat_import"), [items]);

  // Google events mapped as MonthCalendar overlay blocks
  const overlay = useMemo(() => (gcal.events || []).map((e) => ({
    user_id: "me", start: e.start, end: e.end, title: e.title,
    tier: "full_detail", member: { display_name: e.calendar_name },
  })), [gcal.events]);

  const dayItems = useMemo(() => {
    if (!selectedDate) return { plans: [], gevents: [] };
    return {
      plans: visibleItems.filter((e) => e.date === selectedDate),
      gevents: (gcal.events || []).filter((e) => localDay(e.start) === selectedDate),
    };
  }, [selectedDate, visibleItems, gcal.events]);

  return (
    <TabLayout>
      <div className="px-4 pt-6 pb-24">
        <div className="flex items-center gap-2.5 mb-4 pr-28">
          <HomeButton bg={colors.surfaceSecondary} />
          <h1 className="font-display font-bold text-[26px]" style={{ color: colors.onSurface }}>Your calendar</h1>
        </div>

        <div className="flex mb-4">
          <div className="flex rounded-xl p-0.5" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
            <button data-testid="view-list" onClick={() => setView("list")} className="flex items-center gap-1 rounded-lg px-2.5 py-1.5" style={{ background: view === "list" ? colors.brand : "transparent" }}>
              <List size={14} color={view === "list" ? colors.onBrand : colors.onSurfaceTertiary} />
              <span className="font-display font-medium text-[12px]" style={{ color: view === "list" ? colors.onBrand : colors.onSurfaceTertiary }}>List</span>
            </button>
            <button data-testid="view-calendar" onClick={() => setView("calendar")} className="flex items-center gap-1 rounded-lg px-2.5 py-1.5" style={{ background: view === "calendar" ? colors.brand : "transparent" }}>
              <CalendarRange size={14} color={view === "calendar" ? colors.onBrand : colors.onSurfaceTertiary} />
              <span className="font-display font-medium text-[12px]" style={{ color: view === "calendar" ? colors.onBrand : colors.onSurfaceTertiary }}>Calendar</span>
            </button>
          </div>
        </div>

        {hasImports && (
          <button
            data-testid="toggle-chat-imports"
            onClick={() => setShowImports((v) => !v)}
            className="flex items-center gap-2 rounded-full px-3 py-1.5 mb-3"
            style={{ background: showImports ? colors.brandTertiary : colors.surfaceSecondary, border: `1px solid ${showImports ? colors.brand : colors.border}` }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: showImports ? "#F0C060" : colors.muted }} />
            <span className="font-display font-medium text-[12px]" style={{ color: showImports ? colors.onSurface : colors.onSurfaceTertiary }}>
              Added from chat {showImports ? "· shown" : "· hidden"}
            </span>
          </button>
        )}

        {view === "list" ? (
          <>
            <div className="flex gap-2 mb-2.5 overflow-x-auto no-scrollbar">
              <Chip label="All plans" icon={LayoutGrid} active={filter === "all"} onClick={() => setFilter("all")} testID="agenda-filter-all" />
              <Chip label="I'm going" icon={CheckCircle2} active={filter === "going"} color={colors.success} onClick={() => setFilter("going")} testID="agenda-filter-going" />
              <Chip label="Commented" icon={MessageCircle} active={filter === "commented"} onClick={() => setFilter("commented")} testID="agenda-filter-commented" />
            </div>
            <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
              {[["all", "All"], ["today", "Today"], ["week", "This week"], ["month", "This month"]].map(([key, label]) => {
                const active = range === key;
                return (
                  <button key={key} data-testid={`range-${key}`} onClick={() => setRange(key)}
                    className="flex items-center rounded-full py-2 px-3.5 shrink-0 transition-all"
                    style={{ background: active ? colors.brand : colors.surfaceSecondary, border: `1px solid ${active ? colors.brand : colors.border}` }}>
                    <span className="font-display font-medium text-[12.5px]" style={{ color: active ? colors.onBrand : colors.onSurfaceTertiary }}>{label}</span>
                  </button>
                );
              })}
            </div>
            {loading ? (
              <div className="flex justify-center pt-20"><Spinner /></div>
            ) : split.upcoming.length === 0 && split.passed.length === 0 ? (
              <div className="flex flex-col items-center pt-20 px-6 text-center gap-2">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mb-2" style={{ background: colors.brandTertiary }}>
                  <CalendarDays size={28} color={colors.brandSecondary} />
                </div>
                <h2 className="font-display font-bold text-lg" style={{ color: colors.onSurface }}>
                  {range !== "all" ? "Nothing in this range" : filter === "all" ? "Nothing scheduled" : filter === "going" ? "You're not going to anything yet" : "You haven't commented yet"}
                </h2>
                <p className="font-display text-[13.5px]" style={{ color: colors.onSurfaceTertiary }}>{range !== "all" ? "Try a wider range like This month or All." : "Plans from all your groups show up here."}</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {split.upcoming.length > 0 ? (
                  <div>
                    <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mt-1 mb-1 ml-1" style={{ color: colors.muted }} data-testid="upcoming-heading">Upcoming</p>
                    <div className="flex flex-col gap-2">
                      {split.upcoming.map((item) => <AgendaItem key={item.occ_id || item.id} item={item} onClick={() => navigate(`/event/${item.id}`)} />)}
                    </div>
                  </div>
                ) : (
                  <p className="font-display text-[13.5px] pt-4 pb-1 text-center" style={{ color: colors.onSurfaceTertiary }}>Nothing upcoming{range === "all" ? "" : " in this range"}.</p>
                )}
                {split.passed.length > 0 && (
                  <>
                    <button
                      data-testid="toggle-passed"
                      onClick={() => setShowPassed((v) => !v)}
                      className="flex items-center justify-center gap-1.5 rounded-full py-2 px-4 mx-auto mt-2"
                      style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}
                    >
                      <span className="font-display font-medium text-[12.5px]" style={{ color: colors.onSurfaceTertiary }}>
                        {showPassed ? "Hide passed" : `Show passed (${split.passed.length})`}
                      </span>
                    </button>
                    {showPassed && (
                      <div style={{ opacity: 0.7 }} data-testid="passed-section">
                        <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mt-1 mb-1 ml-1" style={{ color: colors.muted }}>Passed</p>
                        <div className="flex flex-col gap-2">
                          {split.passed.map((item) => <AgendaItem key={item.occ_id || item.id} item={item} onClick={() => navigate(`/event/${item.id}`)} />)}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            {!gcal.connected && (
              <div data-testid="gcal-connect-cta" className="rounded-xl p-4 mb-3" style={{ background: colors.surfaceSecondary, border: `1px dashed ${colors.brand}` }}>
                <p className="font-display font-bold text-[14px]" style={{ color: colors.onSurface }}>Connect Google Calendar</p>
                <p className="font-display text-[12.5px] mt-1" style={{ color: colors.onSurfaceTertiary }}>Open any group's Settings → Calendar and connect Google. Your events then show up here automatically.</p>
              </div>
            )}
            {gError && gcal.connected && (
              <button data-testid="gcal-retry" onClick={loadGoogle} className="w-full rounded-xl p-3 mb-3 flex items-center gap-2" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.warning}` }}>
                <RefreshCw size={14} color={colors.warning} />
                <span className="font-display text-[12.5px] flex-1 text-left" style={{ color: colors.warning }}>{gError}</span>
                <span className="font-display font-bold text-[12px]" style={{ color: colors.warning }}>Retry</span>
              </button>
            )}

            <MonthCalendar
              cursor={cursor} setCursor={setCursor} events={visibleItems}
              overlay={overlay} freeMembers={[]}
              selectedDate={selectedDate} setSelectedDate={setSelectedDate}
              themeColor={colors.brand} themeOn={colors.onBrand}
              overlayLabel={gLoading ? "Loading Google…" : "From Google Calendar"}
            />

            {selectedDate && (
              <div className="mt-1">
                <p className="font-display font-medium text-[11.5px] uppercase tracking-wide mb-2 ml-1" style={{ color: colors.muted }}>{longDate(selectedDate)}</p>
                {dayItems.plans.length === 0 && dayItems.gevents.length === 0 ? (
                  <p className="font-display text-[13px] ml-1" style={{ color: colors.faint }}>Nothing on this day.</p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {dayItems.plans.map((item) => <AgendaItem key={item.occ_id || item.id} item={item} onClick={() => navigate(`/event/${item.id}`)} />)}
                    {dayItems.gevents.map((e) => (
                      <div key={e.id} data-testid="gcal-day-event" className="flex items-center gap-3 rounded-xl p-3" style={{ background: colors.surfaceSecondary, border: `1px solid ${colors.border}` }}>
                        <span className="w-1.5 self-stretch rounded-full shrink-0" style={{ background: e.calendar_color || colors.brandSecondary, minHeight: 32 }} />
                        <div className="flex-1 min-w-0">
                          <p className="font-display font-bold text-[13.5px] truncate" style={{ color: colors.onSurface }}>{e.title}</p>
                          <p className="font-display text-[11.5px] truncate" style={{ color: colors.faint }}>{clockOf(e)} · {e.calendar_name}{e.location ? ` · ${e.location}` : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {!selectedDate && (
              <p className="font-display text-[12.5px] text-center mt-2" style={{ color: colors.faint }}>
                {gcal.connected ? `${(gcal.events || []).length} Google event${(gcal.events || []).length === 1 ? "" : "s"} this month · tap a day to see details` : "Tap a day to see your plans"}
              </p>
            )}
          </>
        )}
      </div>
    </TabLayout>
  );
}
